import {
  DEFAULT_THRESHOLD,
  NOISE_FLOOR_ATTACK,
  NOISE_FLOOR_ATTACK_OPEN,
  NOISE_FLOOR_RELEASE,
  NOISE_FLOOR_HOLD_MS,
  NOISE_FLOOR_MAX_TRACK_RATIO,
  AUTO_THRESHOLD_MULTIPLIER,
  AUTO_THRESHOLD_OFFSET,
  AUTO_THRESHOLD_MIN,
  AUTO_THRESHOLD_MAX,
  HYSTERESIS_RATIO,
  INITIAL_NOISE_FLOOR,
} from './types'

const registeredContexts = new WeakSet<AudioContext>()
let workletBlobUrl: string | null = null

function getBlobUrl(): string {
  if (!workletBlobUrl) {
    const blob = new Blob([getWorkletCode()], {type: 'application/javascript'})
    workletBlobUrl = URL.createObjectURL(blob)
  }
  return workletBlobUrl
}

/**
 * Ensures the audio worklet module is registered on the given AudioContext.
 * Safe to call multiple times — registers only once per context.
 */
export async function ensureWorkletModule(ctx: AudioContext): Promise<void> {
  if (!registeredContexts.has(ctx)) {
    await ctx.audioWorklet.addModule(getBlobUrl())
    registeredContexts.add(ctx)
  }
}

/**
 * Returns the AudioWorkletProcessor source code as a JavaScript string.
 *
 * Contains two processors:
 * - `noise-gate` — full noise gate with adaptive threshold and gain smoothing
 * - `rms-meter` — lightweight RMS level meter (no gating)
 *
 * Constants from types.ts are injected via template literal interpolation,
 * keeping a single source of truth for tuning parameters.
 */
function getWorkletCode(): string {
  return `
class NoiseGateWorkletProcessor extends AudioWorkletProcessor {
  constructor() {
    super()
    this._threshold = ${DEFAULT_THRESHOLD}
    this._manualMode = false
    this._noiseFloor = ${INITIAL_NOISE_FLOOR}
    this._autoThreshold = ${AUTO_THRESHOLD_MIN}
    this._gateOpen = false
    this._gateClosedAt = 0
    this._currentGain = 1
    this._lastReportTime = 0

    // Per-sample gain smoothing coefficient (replicates setTargetAtTime with time constant 0.015 s)
    this._smoothingCoeff = 1 - Math.exp(-1 / (sampleRate * 0.015))

    this.port.onmessage = (e) => {
      const d = e.data
      if (d.type === 'config') {
        this._threshold = d.threshold
        this._manualMode = d.manualMode
      } else if (d.type === 'seed') {
        this._noiseFloor = d.noiseFloor
        this._autoThreshold = Math.min(
          Math.max(
            this._noiseFloor * ${AUTO_THRESHOLD_MULTIPLIER} + ${AUTO_THRESHOLD_OFFSET},
            ${AUTO_THRESHOLD_MIN}
          ),
          ${AUTO_THRESHOLD_MAX}
        )
      } else if (d.type === 'reset') {
        this._noiseFloor = ${INITIAL_NOISE_FLOOR}
        this._autoThreshold = ${AUTO_THRESHOLD_MIN}
        this._gateOpen = false
        this._gateClosedAt = 0
      }
    }
  }

  process(inputs, outputs) {
    const input = inputs[0]
    const output = outputs[0]
    if (!input || !input.length || !output || !output.length) return true

    const blockSize = input[0].length
    if (blockSize === 0) return true

    // ── 1. Compute RMS from first channel ──
    const ch0 = input[0]
    let sum = 0
    for (let i = 0; i < blockSize; i++) {
      const s = ch0[i]
      sum += s * s
    }
    const rms = Math.sqrt(sum / blockSize)

    // ── 2. Update adaptive noise floor ──
    const now = currentTime
    if (!this._gateOpen) {
      const elapsedMs = (now - this._gateClosedAt) * 1000
      if (elapsedMs >= ${NOISE_FLOOR_HOLD_MS}) {
        const alpha = rms > this._noiseFloor ? ${NOISE_FLOOR_ATTACK} : ${NOISE_FLOOR_RELEASE}
        this._noiseFloor += alpha * (rms - this._noiseFloor)
      }
    } else if (rms > this._noiseFloor) {
      const cappedRms = Math.min(rms, this._noiseFloor * ${NOISE_FLOOR_MAX_TRACK_RATIO})
      this._noiseFloor += ${NOISE_FLOOR_ATTACK_OPEN} * (cappedRms - this._noiseFloor)
    }

    this._autoThreshold = Math.min(
      Math.max(
        this._noiseFloor * ${AUTO_THRESHOLD_MULTIPLIER} + ${AUTO_THRESHOLD_OFFSET},
        ${AUTO_THRESHOLD_MIN}
      ),
      ${AUTO_THRESHOLD_MAX}
    )

    // ── 3. Gate logic with hysteresis ──
    const effectiveThreshold = this._manualMode ? this._threshold : this._autoThreshold
    const closeThreshold = effectiveThreshold * ${HYSTERESIS_RATIO}
    let targetGain

    if (this._gateOpen) {
      if (rms < closeThreshold) {
        this._gateOpen = false
        this._gateClosedAt = now
        targetGain = 0
      } else {
        targetGain = 1
      }
    } else {
      if (rms >= effectiveThreshold) {
        this._gateOpen = true
        targetGain = 1
      } else {
        targetGain = 0
      }
    }

    // ── 4. Apply per-sample smoothed gain to all channels ──
    const coeff = this._smoothingCoeff
    let gain = this._currentGain
    for (let i = 0; i < blockSize; i++) {
      gain += coeff * (targetGain - gain)
      for (let ch = 0; ch < input.length; ch++) {
        output[ch][i] = input[ch][i] * gain
      }
    }
    this._currentGain = gain

    // ── 5. Report to main thread (~60 Hz) ──
    if (now - this._lastReportTime >= 0.016) {
      this._lastReportTime = now
      this.port.postMessage({ rms: rms, autoThreshold: this._autoThreshold, noiseFloor: this._noiseFloor })
    }

    return true
  }
}

registerProcessor('noise-gate', NoiseGateWorkletProcessor)

class RmsMeterWorkletProcessor extends AudioWorkletProcessor {
  constructor() {
    super()
    this._noiseFloor = ${INITIAL_NOISE_FLOOR}
    this._autoThreshold = ${AUTO_THRESHOLD_MIN}
    this._gateOpen = false
    this._gateClosedAt = 0
    this._lastReportTime = 0
  }

  process(inputs) {
    const input = inputs[0]
    if (!input || !input.length) return true

    const ch0 = input[0]
    const blockSize = ch0.length
    if (blockSize === 0) return true

    let sum = 0
    for (let i = 0; i < blockSize; i++) {
      const s = ch0[i]
      sum += s * s
    }
    const rms = Math.sqrt(sum / blockSize)

    const now = currentTime

    // Adaptive noise floor (same algorithm as noise-gate)
    if (!this._gateOpen) {
      const elapsedMs = (now - this._gateClosedAt) * 1000
      if (elapsedMs >= ${NOISE_FLOOR_HOLD_MS}) {
        const alpha = rms > this._noiseFloor ? ${NOISE_FLOOR_ATTACK} : ${NOISE_FLOOR_RELEASE}
        this._noiseFloor += alpha * (rms - this._noiseFloor)
      }
    } else if (rms > this._noiseFloor) {
      const cappedRms = Math.min(rms, this._noiseFloor * ${NOISE_FLOOR_MAX_TRACK_RATIO})
      this._noiseFloor += ${NOISE_FLOOR_ATTACK_OPEN} * (cappedRms - this._noiseFloor)
    }

    this._autoThreshold = Math.min(
      Math.max(
        this._noiseFloor * ${AUTO_THRESHOLD_MULTIPLIER} + ${AUTO_THRESHOLD_OFFSET},
        ${AUTO_THRESHOLD_MIN}
      ),
      ${AUTO_THRESHOLD_MAX}
    )

    // Hysteresis gate state (for noise floor tracking, no actual gating)
    const closeThreshold = this._autoThreshold * ${HYSTERESIS_RATIO}
    if (this._gateOpen) {
      if (rms < closeThreshold) {
        this._gateOpen = false
        this._gateClosedAt = now
      }
    } else {
      if (rms >= this._autoThreshold) this._gateOpen = true
    }

    if (now - this._lastReportTime >= 0.016) {
      this._lastReportTime = now
      this.port.postMessage({ rms: rms, autoThreshold: this._autoThreshold })
    }

    return true
  }
}

registerProcessor('rms-meter', RmsMeterWorkletProcessor)
`
}
